import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import api from "@/lib/api";
import { useContactModal } from "@/components/ContactModalProvider";
import RichText from "@/components/RichText";

// Repli affiché si aucune offre n’est encore créée dans l’admin.
const FALLBACK_TIERS = [
  { name: "STARTER", price: "À partir de 4 900€", featured: false, features: ["MVP web ou mobile", "1 plateforme cible", "Livraison 4-6 semaines", "30 jours de support"] },
  { name: "GROWTH", price: "À partir de 12 900€", featured: true, features: ["Web + mobile natif", "Backend & API dédiés", "Livraison 8-10 semaines", "90 jours de support"] },
  { name: "SCALE", price: "Sur devis", featured: false, features: ["Plateforme SaaS complète", "Multi-tenant, facturation", "Équipe dédiée continue", "SLA & support prioritaire"] },
];

const money = (v) => {
  if (typeof v !== "number" || v <= 0) return "Sur devis";
  return `À partir de ${v.toLocaleString("fr-FR")}€`;
};

const DEFAULTS = {
  heading: "Des packages clairs, un devis transparent.",
  link_label: "Toutes nos offres",
};

// Cache module : évite de re-fetcher les offres à chaque re-montage de la
// section (navigation SPA). undefined = pas encore chargé, null = API échec.
let _offersCache;

export default function PricingSection({ props = {} }) {
  const t = { ...DEFAULTS, ...props };
  // Cache hit → rendu immédiat sans état de chargement intermédiaire.
  const [offers, setOffers] = useState(_offersCache !== undefined ? _offersCache : null);
  const { open: openContact } = useContactModal();
  const pricingTrackRef = useRef(null);
  const pricingInteractingRef = useRef(false);
  const pricingResumeTimeoutRef = useRef(null);
  const pricingDragRef = useRef({ active: false, startX: 0, startScroll: 0, moved: false });

  useEffect(() => {
    if (_offersCache !== undefined) return; // cache hit — pas d'appel réseau
    api.get("/products", { params: { limit: 100 } })
      .then(({ data }) => {
        const o = Array.isArray(data) ? data : [];
        _offersCache = o;
        setOffers(o);
      })
      .catch(() => {
        _offersCache = [];
        setOffers([]);
      });
  }, []);

  // Tarifs reliés aux offres dynamiques de l’admin (repli sur exemples si vide).
  // On privilégie la catégorie "Formules" pour la grille tarifaire ; à défaut,
  // on prend toutes les offres.
  // Calculé AVANT le second useEffect pour que tiers.length soit disponible
  // comme dépendance du carrousel — le return conditionnel vient après tous les hooks.
  const pricingBase = offers ? offers.filter((o) => o.category_slug === "formules") : [];
  const baseOffers = pricingBase.length ? pricingBase : (offers || []);
  const sortedOffers = [...baseOffers].sort(
    (a, b) => (a.price > 0 ? a.price : Infinity) - (b.price > 0 ? b.price : Infinity)
  );
  const topOffers = sortedOffers.slice(0, 3);
  const anyFeatured = topOffers.some((o) => o.featured);
  // offers===null → chargement → [] (pas d’affichage, carrousel en pause)
  // offers===[]   → pas d’offres → FALLBACK_TIERS
  // offers=[...]  → vraies offres → topOffers mappés
  const tiers = offers === null
    ? []
    : offers.length
      ? topOffers.map((o, i) => ({
          name: (o.name || "Offre").toUpperCase(),
          price: money(o.price),
          featured: anyFeatured ? !!o.featured : topOffers.length >= 3 && i === 1,
          features: (o.description || "").split(/[\n•]/).map((s) => s.trim()).filter(Boolean).slice(0, 4),
          slug: o.slug,
          rawPrice: typeof o.price === "number" ? o.price : null,
        }))
      : FALLBACK_TIERS;

  // Le jeu de cartes est répété PRICING_REPEAT fois pour garantir qu’il reste
  // toujours assez de contenu dupliqué après le point de rebouclage, quel que
  // soit le nombre d’offres : sinon, avec seulement 2 copies, la largeur de
  // rebouclage (scrollWidth/2) peut dépasser la position de scroll maximale
  // autorisée par le navigateur (scrollWidth - clientWidth). Le scroll reste
  // alors bloqué en butée de fin, ce qui donnait l’impression que le
  // défilement automatique repartait en sens inverse vers le début.
  const PRICING_REPEAT = 6;

  // Défilement automatique du carrousel de tarifs, en pause pendant que
  // l’utilisateur interagit (doigt ou souris), reprise peu après le relâchement.
  // IMPORTANT : ce useEffect est toujours appelé (pas de return conditionnel
  // avant lui) — il retourne immédiatement quand tiers est vide (chargement).
  useEffect(() => {
    const track = pricingTrackRef.current;
    if (!track || !tiers.length) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;

    const durationPerSet = Math.max(tiers.length, 1) * 9; // secondes pour parcourir un jeu de cartes
    let raf;
    let last = performance.now();

    const step = (now) => {
      const dt = (now - last) / 1000;
      last = now;
      if (!pricingInteractingRef.current) {
        const setWidth = track.scrollWidth / PRICING_REPEAT;
        if (setWidth > 0) track.scrollLeft += (setWidth / durationPerSet) * dt;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [tiers.length]);

  // Pendant le chargement on ne rend rien pour éviter le flash des tiers
  // agence par défaut. Placé APRÈS tous les hooks pour respecter les règles
  // de React (pas de violation d'ordre d'appel entre les rendus).
  if (offers === null) return null;

  const pricingLoopScroll = () => {
    const track = pricingTrackRef.current;
    if (!track) return;
    const setWidth = track.scrollWidth / PRICING_REPEAT;
    if (setWidth > 0 && track.scrollLeft >= setWidth) track.scrollLeft -= setWidth;
  };

  const pricingPause = () => {
    pricingInteractingRef.current = true;
    if (pricingResumeTimeoutRef.current) clearTimeout(pricingResumeTimeoutRef.current);
  };
  const pricingScheduleResume = (delay = 1000) => {
    if (pricingResumeTimeoutRef.current) clearTimeout(pricingResumeTimeoutRef.current);
    pricingResumeTimeoutRef.current = setTimeout(() => {
      pricingInteractingRef.current = false;
    }, delay);
  };
  const pricingPointerDown = (e) => {
    pricingPause();
    if (e.pointerType === "mouse") {
      pricingDragRef.current = { active: true, startX: e.clientX, startScroll: pricingTrackRef.current.scrollLeft, moved: false, pointerId: e.pointerId };
      // Pas de setPointerCapture ici : le capturer dès le pointerdown redirige
      // aussi le futur événement "click" vers le track (au lieu du lien/bouton
      // réellement cliqué), ce qui casse silencieusement "Choisir cette offre"
      // même sur un simple clic sans glissement. On ne capture qu'une fois un
      // vrai glissement détecté, dans pricingPointerMove ci-dessous.
    }
  };
  const pricingPointerMove = (e) => {
    if (!pricingDragRef.current.active) return;
    const dx = e.clientX - pricingDragRef.current.startX;
    if (Math.abs(dx) > 4 && !pricingDragRef.current.moved) {
      pricingDragRef.current.moved = true;
      pricingTrackRef.current.setPointerCapture(pricingDragRef.current.pointerId);
    }
    pricingTrackRef.current.scrollLeft = pricingDragRef.current.startScroll - dx;
  };
  const pricingEndDrag = () => {
    pricingDragRef.current.active = false;
    pricingScheduleResume();
  };
  const pricingClickCapture = (e) => {
    if (pricingDragRef.current.moved) {
      e.preventDefault();
      pricingDragRef.current.moved = false;
    }
  };

  return (
    <section id="tarifs" className="max-w-[1240px] mx-auto px-6 md:px-10 pt-section-top md:pt-section-top-md scroll-mt-28 animate-fade-in">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <RichText
          as="h2"
          className="font-display font-semibold text-[30px] md:text-[34px] leading-tight tracking-[-0.02em] text-ink max-w-lg"
          html={t.heading}
        />
        <Link to="/boutique" className="text-[14px] font-medium text-gold hover:text-ink transition-colors inline-flex items-center gap-1.5">
          {t.link_label} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      <div
        className="relative mt-12 -mx-6 md:-mx-10"
        style={{ WebkitMaskImage: "linear-gradient(90deg, transparent, black 5%, black 95%, transparent)", maskImage: "linear-gradient(90deg, transparent, black 5%, black 95%, transparent)" }}
      >
        <div
          ref={pricingTrackRef}
          onScroll={pricingLoopScroll}
          onPointerDown={pricingPointerDown}
          onPointerMove={pricingPointerMove}
          onPointerUp={pricingEndDrag}
          onPointerCancel={pricingEndDrag}
          onClickCapture={pricingClickCapture}
          className="flex gap-6 px-6 md:px-10 overflow-x-auto no-scrollbar select-none cursor-grab active:cursor-grabbing"
          // Pas de WebkitOverflowScrolling:"touch" ici : cette propriété active le
          // scroll "momentum" compositeur sur Safari/iOS, connu pour entrer en
          // conflit avec les écritures JS de scrollLeft (l'auto-scroll se fige
          // silencieusement sur iOS réel). Obsolète de toute façon depuis iOS 13
          // (le momentum scroll est natif par défaut sur tout overflow:auto).
          style={{ scrollBehavior: "auto", touchAction: "pan-x" }}
        >
          {Array.from({ length: PRICING_REPEAT }, () => tiers).flat().map((t, i) => (
            <div
              key={`${i}-${t.slug || t.name}`}
              className={`relative rounded-card p-7 flex flex-col w-[300px] sm:w-[340px] shrink-0 transition-transform duration-300 hover:-translate-y-1 hover:shadow-card ${
                t.featured
                  ? "border-2 border-gold bg-white"
                  : "border-control border-stone bg-white"
              }`}
              style={
                t.featured
                  ? { background: "linear-gradient(180deg,rgba(99,91,255,0.06),rgba(56,189,248,0.03))", boxShadow: "0 20px 50px rgba(99,91,255,0.14)" }
                  : undefined
              }
            >
              {t.featured && (
                <span className="absolute -top-3 left-7 text-[11px] font-semibold text-white px-3 py-1 rounded-full" style={{ background: "linear-gradient(135deg,#635BFF,#38BDF8)" }}>
                  Le plus choisi
                </span>
              )}
              <div className="text-[13px] font-semibold tracking-[0.06em] text-ash">{t.name}</div>
              <div className={`font-display font-semibold text-[28px] mt-3 ${t.featured ? "text-gold" : "text-ink"}`}>{t.price}</div>
              <div className="h-px bg-stone my-5" />
              <ul className="space-y-3 flex-1">
                {(t.features.length ? t.features : ["Sur mesure selon votre besoin"]).map((f, j) => (
                  <li key={j} className="flex items-start gap-2.5 text-[14px] text-graphite">
                    <Check className="h-4 w-4 text-copper shrink-0 mt-0.5" strokeWidth={2.4} />
                    {f}
                  </li>
                ))}
              </ul>
              {t.slug ? (
                <Link to={`/produit/${t.slug}`} className={`mt-6 text-center py-3 rounded-btn text-[14px] font-semibold transition-opacity hover:opacity-90 ${t.featured ? "bg-ink text-white" : "border-control border-stone text-ink hover:border-ink"}`}>
                  Choisir cette offre
                </Link>
              ) : (
                <button type="button" onClick={() => openContact({ items: [{ name: t.name, quantity: 1, price: t.rawPrice, slug: t.slug }], source: "tarifs" })} className={`mt-6 text-center py-3 rounded-btn text-[14px] font-semibold transition-opacity hover:opacity-90 ${t.featured ? "bg-ink text-white" : "border-control border-stone text-ink hover:border-ink"}`}>
                  Demander un devis
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
